import {
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';

import { ElementType } from 'react';
import { useTranslation } from 'react-i18next';
import { VerticalEllipsis } from '@/client/components/icons/individual/VerticalEllipsis';

// TODO: Replace all warmNeutral ellipsis menus with this component: WarmNeutralTableActionsMenu.tsx, WarmNeutralTableActionsMenuNew.tsx

interface WarmNeutralEllipsisMenuProps {
  /** The options to display in the menu */
  options: {
    label: string;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    /**
     * React key for the option. Pass one whenever labels can collide (e.g. a
     * per-context item named after user content) — `label` is the fallback.
     */
    id?: string;
    icon?: ElementType;
    iconColor?: string;
    isDisabled?: boolean;
    disabledTooltipText?: string;
    isHidden?: boolean;
  }[];
  /** Whether the menu is disabled @default false */
  isDisabled?: boolean;
  /** The orientation of the icon @default vertical */
  iconOrientation?: 'horizontal' | 'vertical';
}

export default function WarmNeutralEllipsisMenu({
  options,
  isDisabled = false,
  iconOrientation = 'vertical',
}: WarmNeutralEllipsisMenuProps) {
  const { t } = useTranslation();
  const hasNoAvailableOptions = options.every((option) => option.isHidden);

  return (
    <Menu>
      <MenuButton
        isDisabled={isDisabled || hasNoAvailableOptions}
        size="sm"
        as={IconButton}
        aria-label={t('admin.contentAnalytics.rowActionsAriaLabel') ?? ''}
        variant="ghost"
        color="neutral.400"
        icon={
          <VerticalEllipsis
            transform={iconOrientation === 'horizontal' ? 'rotate(90deg)' : 'none'}
          />
        }
        _active={{
          backgroundColor: 'neutral.50',
        }}
        _hover={{
          backgroundColor: 'neutral.50',
        }}
        onClick={(event) => event.stopPropagation()}
      />

      {/* Portal so the list escapes overflow-clipped table containers. */}
      <Portal>
        <MenuList
          border="1px solid"
          borderColor="warmNeutral.200"
          borderRadius="6px"
          paddingX={4}
        >
          {options.map((option) => {
            if (option.isHidden) return null;

            return (
              <Tooltip
                key={option.id ?? option.label}
                hasArrow
                placement="auto-start"
                isDisabled={!option.isDisabled}
                label={option.disabledTooltipText}
              >
                <MenuItem
                  key={option.id ?? option.label}
                  onClick={option.onClick}
                  isDisabled={option.isDisabled}
                  // Only when disabled: Chakra otherwise renders a natively
                  // disabled <button>, which swallows the hover the Tooltip above
                  // needs — so `disabledTooltipText` never appeared for anyone.
                  // aria-disabled keeps the item hoverable and focusable (and
                  // announced) while Chakra still suppresses its onClick.
                  isFocusable={option.isDisabled}
                  fontSize="14px"
                  fontWeight={500}
                  color="baseBlack"
                  paddingY={4}
                  borderBottom="1px solid"
                  borderBottomColor="warmNeutral.200"
                  _hover={{
                    backgroundColor: 'warmNeutral.0',
                  }}
                  _active={{
                    backgroundColor: 'warmNeutral.0',
                  }}
                  _focus={{
                    backgroundColor: 'warmNeutral.0',
                  }}
                  _last={{
                    borderBottom: 'none',
                  }}
                >
                  <HStack spacing={2}>
                    {option.icon && <Icon as={option.icon} />}

                    <Text>{option.label}</Text>
                  </HStack>
                </MenuItem>
              </Tooltip>
            );
          })}
        </MenuList>
      </Portal>
    </Menu>
  );
}
